import type { DaySlots, LinkAvailability, LinkWindow, OpenSlot } from '@/api/types';

/** v9 §A1 personal links: handles, availability presets and slot grouping. */

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/** Mirrors the server: 3–20 of a-z 0-9 _ . (lower-cased, a leading @ dropped), no dot at either end or "..". */
export function normalizeHandle(raw: string): string {
  return raw.trim().replace(/^@/, '').toLowerCase();
}
export function handleProblem(h: string): string | null {
  if (!/^[a-z0-9_.]{3,20}$/.test(h)) return 'Use 3–20 letters, numbers, _ or .';
  if (h.startsWith('.') || h.endsWith('.') || h.includes('..')) return 'A handle can’t start or end with a dot.';
  return null;
}
/** A first guess from the display name ("Maya K" → "mayak"). */
export function suggestHandle(name: string): string {
  const base = name
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 16);
  return base.length >= 3 ? base : `${base}date`.slice(0, 16);
}

/** The one-liner: ≤ 80 characters, no links, numbers or @handles (the server has the final word). */
export function lineProblem(line: string): string | null {
  if (line.length > 80) return 'Keep it to 80 characters.';
  if (/(https?:\/\/|www\.|\.[a-z]{2,}\b|@)/i.test(line)) return 'No links or handles — just a line about you.';
  if (/\d/.test(line)) return 'No numbers — keep contact details for later.';
  return null;
}

/** "HH:MM" every 30 minutes (end may be 24:00). */
export function halfHours(includeEnd = false): string[] {
  const out: string[] = [];
  for (let m = 0; m <= 24 * 60; m += 30) {
    if (m === 24 * 60 && !includeEnd) break;
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return out;
}

/** "21:30" → "9:30 pm". */
export function clockLabel(hhmm: string): string {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  if (h === 24) return 'midnight';
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${suffix}` : `${h12} ${suffix}`;
}

export interface Preset {
  id: 'weeknights' | 'weekends' | 'evenings';
  label: string;
  windows: LinkWindow[];
}
export const PRESETS: Preset[] = [
  { id: 'weeknights', label: 'Weeknights 8–10 pm', windows: [1, 2, 3, 4, 5].map((day) => ({ day, start: '20:00', end: '22:00' })) },
  { id: 'weekends', label: 'Weekend afternoons', windows: [0, 6].map((day) => ({ day, start: '14:00', end: '18:00' })) },
  { id: 'evenings', label: 'Every evening 7–11 pm', windows: [0, 1, 2, 3, 4, 5, 6].map((day) => ({ day, start: '19:00', end: '23:00' })) },
];

export function defaultAvailability(tz: string): LinkAvailability {
  return { windows: [], lengths: [30], max_per_day: 2, timezone: tz };
}

/** Windows sorted by day then start, for display and save. */
export function sortWindows(ws: LinkWindow[]): LinkWindow[] {
  return [...ws].sort((a, b) => a.day - b.day || a.start.localeCompare(b.start));
}

/** A window is valid when start < end (both on 30-minute steps). */
export function windowProblem(w: LinkWindow): string | null {
  return w.start >= w.end ? 'Each window needs a start before its end.' : null;
}

/** "Mon–Fri 8–10 pm · Sat, Sun 2–6 pm" (compact summary of windows). */
export function summarizeWindows(ws: LinkWindow[]): string {
  if (ws.length === 0) return 'No times set yet';
  const byTime = new Map<string, number[]>();
  for (const w of sortWindows(ws)) {
    const k = `${w.start}-${w.end}`;
    byTime.set(k, [...(byTime.get(k) ?? []), w.day]);
  }
  return [...byTime.entries()]
    .map(([k, days]) => {
      const [s = '', e = ''] = k.split('-');
      return `${daysLabel(days)} ${clockLabel(s)}–${clockLabel(e)}`;
    })
    .join(' · ');
}

function daysLabel(days: number[]): string {
  const d = [...new Set(days)].sort((a, b) => a - b);
  if (d.length === 7) return 'Every day';
  if (d.join() === '1,2,3,4,5') return 'Mon–Fri';
  if (d.join() === '0,6') return 'Weekends';
  return d.map((x) => DAYS[x]).join(', ');
}

/** Open slots regrouped by the viewer's local day (the server groups by the host's). */
export function slotsByLocalDay(days: DaySlots[], length?: number): { key: string; label: string; slots: OpenSlot[] }[] {
  const out = new Map<string, { key: string; label: string; slots: OpenSlot[] }>();
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86_400_000);
  for (const d of days) {
    for (const s of d.slots) {
      if (length && !s.lengths.includes(length as 30 | 60)) continue;
      const t = new Date(s.starts_at);
      const key = `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}`;
      const same = (x: Date) => x.getFullYear() === t.getFullYear() && x.getMonth() === t.getMonth() && x.getDate() === t.getDate();
      const label = same(now) ? 'Today' : same(tomorrow) ? 'Tomorrow' : t.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });
      if (!out.has(key)) out.set(key, { key, label, slots: [] });
      out.get(key)!.slots.push(s);
    }
  }
  return [...out.values()];
}

/** "8:30 PM" in the viewer's zone. */
export function slotTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** The viewer's IANA zone (falls back to UTC). */
export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** "popin.buzz/@maya" for display (the full URL is what's copied). */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

/** The Instagram / Hinge / Bumble bio line. */
export function bioLine(url: string): string {
  return `First date on video? ${url} 🎥`;
}

// ---- a picked slot survives sign-in + express onboarding (this tab only)
const PICK_KEY = 'popin.linkPick';
export interface LinkPick {
  handle: string;
  starts_at: string;
  length_min: 30 | 60;
}
export function rememberPick(p: LinkPick): void {
  try {
    sessionStorage.setItem(PICK_KEY, JSON.stringify(p));
  } catch {
    /* storage blocked: they pick again */
  }
}
export function recallPick(handle: string): LinkPick | null {
  try {
    const raw = sessionStorage.getItem(PICK_KEY);
    const v = raw ? (JSON.parse(raw) as LinkPick) : null;
    return v && v.handle === handle.toLowerCase() && typeof v.starts_at === 'string' && (v.length_min === 30 || v.length_min === 60) ? v : null;
  } catch {
    return null;
  }
}
export function forgetPick(): void {
  try {
    sessionStorage.removeItem(PICK_KEY);
  } catch {
    /* ignore */
  }
}
